import React, { useRef, useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import cmtCategoryActions from "../../../_actions/_qmsActions/cmtCategoryActions";
import qmsDocumentActions from "../../../_actions/_qmsActions/qmsDocumentActions";
import qmsFrameworkChangeActions from "../../../_actions/_qmsActions/qmsFrameworkChangeActions";
import { ISSUE_PRIORITY } from "../../../_constants/qmsConstants";
import cmtIssueActions from "../../../_actions/_qmsActions/cmtIssueActions";
import userActions from "../../../_actions/userActions";
import qmsUserActions from "../../../_actions/_qmsActions/qmsUserActions";
import qmsDocumentTypeActions from "../../../_actions/_qmsActions/qmsDocumentTypeActions";
import _ from "lodash";
import unitActions from "../../../_actions/unitActions";
import AlertModal from "../../_common/AlertModal";
import { QMS_DOCTYPE_SYMBOL } from "../_common/qmsConstants";
import { getQmsDocumentLabel } from "../_common/qmsUtilities";
import CmtLinkedDocumentEditor from "./CmtLinkedDocumentEditor";
import BadgeContainer from "../../_common/BadgeContainer";
import Badge from "../../_common/Badge";

/**
 * Modal for initializing (adding) an issue.
 * QmsUser may not exist for the current ProDmsUser, so use ProDmsUser's data
 * stored in redux's state.authentication.user until submit.
 * @param {Object} props
 * @returns {JSX.Element}
 */
export default function CmtModal_AddIssue(props) {
  const { modalState, toggleModal, tabId, divisionId } = props;

  const dispatch = useDispatch();
  const isInitialRender = useRef(true); // To skip the first render in useEffect()
  const qmsUser = useRef(null);
  const newIssueData = useRef(null);

  const adding = useSelector((state) => state.qms.adding);
  const isAdding = useRef(adding); // To capture previous 'adding' value in useEffect()

  const proDmsUser = useSelector((state) => state.authentication.user || null);
  const userFunctionalRoles = useSelector(
    (state) => state.qms.userFunctionalRoles || null
  );
  const fetching = useSelector((state) => state.qms.fetching);

  const [email, setEmail] = useState(
    proDmsUser.employeeNo ? `${proDmsUser.employeeNo}@dot.ca.gov` : ""
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedUserFunctionalRole, setUserFunctionalRole] = useState(null);
  const [subject, setSubject] = useState("");
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [selectedPriority, setPriority] = useState(null);
  const [description, setDescription] = useState("");
  const [proposedChanges, setProposedChanges] = useState("");
  const [linkedDocuments, setLinkedDocuments] = useState([]);
  const [otherLinkedDocuments, setOtherLinkedDocuments] = useState("");
  const [files, setFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({
    email: null,
    phoneNumber: null,
    role: null,
    subject: null,
    description: null,
    proposedChanges: null,
  });
  const [status_saving, setStatus_saving] = useState(false);
  const [showAlert_submitSuccess, _setShowAlert_submitSuccess] = useState(false);
  const toggleAlert_submitSuccess = () =>
    _setShowAlert_submitSuccess(!showAlert_submitSuccess);

  // Initialize
  useEffect(() => {
    const getQmsUser = async (proDmsUserId) => {
      qmsUser.current = await dispatch(qmsUserActions.getByProDmsUserId(proDmsUserId));

      // If QmsUser is found, pre-set email and phone. (Wait on unit and functional role until
      // they are fetched.)
      if (qmsUser.current) {
        setEmail(qmsUser.current.email);
        setPhoneNumber(qmsUser.current.phoneNumber);
      }
    };

    setPriorityOptions(
      Object.values(ISSUE_PRIORITY).map((ip) => ({
        label: ip.displayName,
        value: ip.value,
        name: ip.name,
      }))
    );

    dispatch(unitActions.getAll(null, tabId));
    dispatch(qmsUserActions.getUserFunctionalRoles(divisionId));
    getQmsUser(proDmsUser.userId);
  }, []);

  useEffect(() => {
    if (adding) isAdding.current = adding;
  }, [adding]);

  // When finished adding new issue, show success message, and refresh CmtList
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (isAdding.current && !adding) {
      toggleAlert_submitSuccess();
    }
  }, [adding]);

  // Dispatch add when status_saving changes
  useEffect(() => {
    if (status_saving && newIssueData.current) {
      // const add = async () => {
      //   const formData = new FormData();
      //   files.forEach((file, i) => formData.append(i, file));
      //   Object.entries(newIssueData.current).forEach(([key, value]) => {
      //     formData.append(key, value);
      //   });
      //   console.log("dispatching");
      //   await dispatch(cmtIssueActions.add(formData));
      // };
      // add();
      dispatch(cmtIssueActions.getAll(divisionId));
      setStatus_saving(false);
      toggleAlert_submitSuccess();
    }
  }, [status_saving, newIssueData.current]);

  const userFunctionalRoleOptions = useMemo(
    () =>
      (userFunctionalRoles || [])
        .filter((ufr) => ufr.value !== 0)
        .map((ufr) => ({
          value: ufr.value,
          label: ufr.displayName,
        })),
    [userFunctionalRoles]
  );

  const formIsIncomplete = () =>
    Object.values(formErrors).some((e) => e) ||
    !email ||
    !phoneNumber ||
    !selectedUserFunctionalRole ||
    !description ||
    !proposedChanges;

  const validateForm = (fieldName) => {
    let standardError = "Required";
    switch (fieldName) {
      case "email":
        setFormErrors({ ...formErrors, email: email ? null : standardError });
        break;
      case "phoneNumber":
        setFormErrors({ ...formErrors, phoneNumber: phoneNumber ? null : standardError });
        break;
      case "role":
        setFormErrors({
          ...formErrors,
          role: selectedUserFunctionalRole ? null : standardError,
        });
        break;
      case "subject":
        setFormErrors({ ...formErrors, subject: subject ? null : standardError });
        break;
      case "description":
        setFormErrors({ ...formErrors, description: description ? null : standardError });
        break;
      case "proposedChanges":
        setFormErrors({
          ...formErrors,
          proposedChanges: proposedChanges ? null : standardError,
        });
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => validateForm(e.target.name);
  const handleBlur_select = (fieldName) => () => validateForm(fieldName);

  const handleUpdate_linkedDocuments = (updatedLdData) => {
    if (!updatedLdData) return;
    setLinkedDocuments(updatedLdData ? updatedLdData.linkedDocuments || [] : []);
    setOtherLinkedDocuments(updatedLdData ? updatedLdData.otherLinkedDocs || "" : "");
  };

  const handleChange_fileInput = (e) => {
    // Must shallow-copy
    let existFiles = [...files];
    const newFiles = Array.from(e.target.files);
    const _files = existFiles.concat(newFiles);
    setFiles(_files);
  };

  const removeFileToUpload = (fileIndex) => {
    // Must shallow-copy
    let _files = [...files];
    _files.splice(fileIndex, 1);
    setFiles(_files);
  };

  const handleClick_submit = async (e) => {
    e.preventDefault();
    if (formIsIncomplete()) return;

    const user = await dispatch(userActions.getInfoById(proDmsUser.userId));

    // Abort if the current user's ProDMS info couldn't be fetched - without it, the
    // QmsUser self-registration below would dereference undefined fields and throw.
    if (!qmsUser.current && !user) return;

    const qmsUserId = qmsUser.current
      ? qmsUser.current.qmsUserId
      : await dispatch(
          qmsUserActions.add({
            qmsUserId: 0,
            divisionId,
            userRoleValue: 1, // Default to lowest level
            userFunctionalRoleValue: selectedUserFunctionalRole.value || 0,
            firstName: user.firstName,
            middleInitial: user.middleInitial,
            lastName: user.lastName,
            employeeNo: user.employeeNo,
            email,
            phoneNumber,
            proDmsUserId: proDmsUser.userId,
            proDmsUserUnitId: proDmsUser.unitId,
            district: user.unit.district,
            unitNumber: user.unit.unitNumber,
          })
        );

    // Abort if the QmsUser could not be found or created - submitting the issue
    // without a valid initiator would silently succeed and leave the user without
    // a QmsUser record (and therefore without QMS access) after refresh.
    if (!qmsUserId) return;

    // If email, phone number or functional role is updated, dispatch an update for
    // QmsUser prior to posting the new issue
    if (
      qmsUser.current &&
      (qmsUser.current.email !== email || qmsUser.current.phoneNumber !== phoneNumber)
    ) {
      dispatch(
        qmsUserActions.update({
          ...qmsUser.current,
          email,
          phoneNumber,
          userFunctionalRoleValue: selectedUserFunctionalRole.value,
        })
      );
    }
    newIssueData.current = {
      divisionId,
      priority: selectedPriority ? selectedPriority.name : "LOW", // If unselected, default to "LOW"
      initiatedByQmsUserId: qmsUserId,
      primaryOwnerId: null, // To be set by QMR during intake
      parentIssueId: null, // Cannot use this modal to create a child
      subject,
      description,
      proposedChanges,
      categoryIds: [1, 2],
      linkedDocuments: (linkedDocuments || []).map((ld) => ({
        qmsDocumentId: ld.qmsDocumentId,
        sections: ld.sections,
      })),
      otherLinkedDocuments,
      deadline: null,
      qmsFrameworkChangeId: null,
      stagePrepopulationType: "none",
      appliedWorkflowId: null,
      assignToQmsTeamId: null,
      initialStageTitle: "",
      skipIntakeReview: false,
    };

    let formData = new FormData();
    files.forEach((file, i) => formData.append(i, file));
    Object.entries(newIssueData.current).forEach(([key, value]) => {
      if (value === null || value === undefined) return; // Omit so server keeps the DTO default instead of binding the literal "null"/"undefined" string
      if (Array.isArray(value)) {
        value.forEach((v, i) => {
          if (v !== null && typeof v === "object" && !Array.isArray(v)) {
            Object.entries(v).forEach(([oKey, oValue]) => {
              formData.append(`${key}[${i}].${oKey}`, oValue);
            });
          } else formData.append(`${key}[${i}]`, v);
        });
      } else formData.append(key, value);
    });
    await dispatch(cmtIssueActions.add(formData));
  };

  const handleAlert_submitSuccess = () => (e) => {
    e.preventDefault();
    dispatch(cmtIssueActions.getAll(divisionId));
    toggleAlert_submitSuccess();
    toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <>
      <form
        id="cmtAddIssueForm"
        onSubmit={handleClick_submit}
        autoComplete="off"
        encType="multipart/form-data"
      >
        <Modal
          isOpen={modalState.showModal}
          toggle={toggleModal}
          fade={false}
          size={"xl"}
        >
          <ModalHeader
            toggle={toggleModal}
            className="bg-info text-white"
            close={closeBtn}
          >
            <FontAwesomeIcon icon="plus" className="fa-fw mr-2" />
            Add New Issue
          </ModalHeader>
          <ModalBody>
            <div className="card mb-2">
              <div className="card-body p-3">
                <h5 className="text-muted">Initiator Info</h5>
                <div className="row mb-2">
                  <div className="form-group col-6 mb-0 pr-2">
                    <label htmlFor="name" className="mb-0">
                      <b>Name/Unit: </b>
                      (based on your current login)
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      className="form-control"
                      value={`${proDmsUser.fullName || ""} (${proDmsUser.distUnit})`}
                      onChange={() => {}}
                      disabled
                    />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="form-group col-md-4 mb-0 pr-2">
                    <label htmlFor="email" className="mb-0">
                      <b>Email:*</b>
                    </label>
                    <input
                      type="text"
                      name="email"
                      id="email"
                      className="form-control"
                      value={email}
                      onBlur={handleBlur}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {formErrors.email && (
                      <span className="error-small">
                        <small>{formErrors.email}</small>
                      </span>
                    )}
                  </div>
                  <div className="form-group col-md-4 mb-0 pr-2 pl-0">
                    <label htmlFor="phoneNumber" className="mb-0 font-weight-bold">
                      Phone:*
                    </label>
                    <input
                      type="text"
                      name="phoneNumber"
                      id="phoneNumber"
                      maxLength={10}
                      className="form-control"
                      value={phoneNumber || ""}
                      onBlur={handleBlur}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                    {formErrors.phoneNumber && (
                      <span className="error-small">
                        <small>{formErrors.phoneNumber}</small>
                      </span>
                    )}
                  </div>
                  <div className="form-group col-md-4 mb-0 pl-0">
                    <label htmlFor="role" className="mb-0 font-weight-bold">
                      Functional Role:*
                    </label>
                    <Select
                      name="role"
                      id="role"
                      value={selectedUserFunctionalRole}
                      options={userFunctionalRoleOptions}
                      onBlur={handleBlur_select("role")}
                      onChange={(selected) => setUserFunctionalRole(selected)}
                      isClearable
                      styles={{
                        // Fixes the overlapping problem of the component
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                    />
                    {formErrors.role && (
                      <span className="error-small">
                        <small>{formErrors.role}</small>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="card mb-2">
              <div className="card-body p-3">
                <h5 className="text-muted">Issue Identification</h5>
                <div className="row mb-2">
                  <div className="form-group col-md-8 mb-0 pr-1">
                    <label htmlFor="subject" className="mb-0 font-weight-bold">
                      Subject:*
                    </label>
                    <input
                      type="text"
                      name="subject"
                      id="subject"
                      className="form-control"
                      value={subject || ""}
                      maxLength="150"
                      onBlur={handleBlur}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                    {formErrors.subject && (
                      <span className="error-small">
                        <small>{formErrors.subject}</small>
                      </span>
                    )}
                  </div>
                  <div className="form-group col-md-4 mb-0 pl-1">
                    <label htmlFor="priority" className="mb-0">
                      Priority:
                    </label>
                    <Select
                      name="priority"
                      id="priority"
                      value={selectedPriority}
                      options={priorityOptions}
                      onChange={(selectedPriority) => setPriority(selectedPriority)}
                      isClearable
                      styles={{
                        // Fixes the overlapping problem of the component
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="form-group col-12 mb-0">
                    <label htmlFor="description" className="mb-0 font-weight-bold">
                      Description of the issue or improvement needed:*
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      value={description}
                      className="form-control"
                      onBlur={handleBlur}
                      onChange={(e) => setDescription(e.target.value)}
                      rows="4"
                    />
                    {formErrors.description && (
                      <span className="error-small">
                        <small>{formErrors.description}</small>
                      </span>
                    )}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="form-group col-12 mb-0">
                    <label htmlFor="proposedChanges" className="mb-0 font-weight-bold">
                      Proposed change(s) and its justification and benefits:*
                    </label>
                    <textarea
                      name="proposedChanges"
                      id="proposedChanges"
                      value={proposedChanges}
                      onBlur={handleBlur}
                      onChange={(e) => setProposedChanges(e.target.value)}
                      className="form-control"
                      rows="4"
                    />
                    {formErrors.proposedChanges && (
                      <span className="error-small">
                        <small>{formErrors.proposedChanges}</small>
                      </span>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="form-group col-12 mb-0">
                    <label className="mb-0">
                      Impacted or relevant documented information (if applicable):
                    </label>
                    <div className="card">
                      <div className="card-body p-3">
                        <CmtLinkedDocumentEditor
                          divisionId={divisionId}
                          data={null}
                          onUpdate={handleUpdate_linkedDocuments}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card mb-2">
              <div className="card-body p-3">
                <div className="row">
                  <div className="form-group col-12 mb-2">
                    <label htmlFor="file" className="mb-0">
                      Attachments: (NOT linked documents. QMR uploads linked documents)
                    </label>
                    <div className="custom-file">
                      <input
                        type="file"
                        id="file"
                        name="file"
                        className="custom-file-input"
                        onChange={handleChange_fileInput}
                        accept=".docx,.xlsx,.txt,.csv,.pdf,.jpg,.jpeg,.gif"
                        multiple
                      />
                      <label htmlFor="file" className="custom-file-label">
                        Choose files...
                      </label>
                    </div>
                  </div>
                </div>
                {files.length > 0 && (
                  <div className="row">
                    <div className="col">
                      <BadgeContainer>
                        {files.map((f, index) => (
                          <Badge
                            className="mr-1"
                            key={`fileLabelIndex_${index}`}
                            itemId={index}
                            clickItem={() => {}}
                            deleteItem={removeFileToUpload}
                            label={f.name}
                            showDelete={true}
                          />
                        ))}
                      </BadgeContainer>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="alert alert-info mb-0 py-1">
              <FontAwesomeIcon icon="info-circle" className="fa-fw mr-2" />
              This issue will be submitted to the QMR, who will then acknowledge receipt
              and review.
            </div>
          </ModalBody>
          <ModalFooter className="bg-lightgray">
            <div className="form-group form-row form-inline float-right mb-0">
              <input
                type="submit"
                name="submit"
                id="submit"
                value="Submit"
                form="cmtAddIssueForm"
                className="btn btn-primary mr-2 font-weight-bold"
                onClick={handleClick_submit}
                disabled={formIsIncomplete()}
              />
              {fetching && (
                <span className="mr-2 mt-1">
                  <FontAwesomeIcon
                    icon="spinner"
                    className="loading-spinner"
                    style={{ color: "white" }}
                  />
                </span>
              )}
              <button
                type="button"
                name="cancel"
                id="cancel"
                className="btn btn-secondary"
                onClick={toggleModal}
              >
                Cancel
              </button>
            </div>
          </ModalFooter>
        </Modal>
      </form>
      {showAlert_submitSuccess && (
        <AlertModal
          alertTitle={"Issue Submitted"}
          alertTitleIcon="check"
          alertTitleClass="bg-success text-white"
          alertText={"A new issue has been created and submitted for QMR's review."}
          acceptCaption={"OK"}
          acceptClass={"primary"}
          provideReject={false}
          handleAlertAction={handleAlert_submitSuccess}
          toggleAlertModal={toggleAlert_submitSuccess}
          showAlertModal={showAlert_submitSuccess}
        />
      )}
    </>
  );
}
